import { Upload } from 'lucide-react';

interface IProps {
  bulkImported?: boolean | null;
}

/**
 * Three-state badge for the `bulk_imported` flag on a Policy.
 *
 * Backend source of truth: `policies.bulk_imported` is a nullable boolean.
 *
 * <ul>
 *   <li><code>true</code>  → "Bulk" indigo pill. Created via the broker bulk
 *       import (broker-upload pipeline OR internal-direct upload). Commission
 *       is suppressed for these policies until they renew.</li>
 *   <li><code>false</code> → "Manual" faint label. Created through the
 *       regular quote / issuance / API flow.</li>
 *   <li><code>null</code>  → "-" dash. Pre-flag legacy row — we genuinely do
 *       not know how it was created. The migration set every pre-existing
 *       false row to null, so this state only applies to historical data.</li>
 * </ul>
 */
const BulkUploadBadge = ({ bulkImported }: IProps) => {
  if (bulkImported === true) {
    return (
      <div
        className="px-2 border border-indigo-700 text-xs flex h-[24px] w-fit items-center justify-center text-indigo-700 rounded-full"
        title="Created via broker bulk import — commission accrues only on renewal"
      >
        <Upload className="inline-block w-3 h-3 mr-1" />
        Bulk
      </div>
    );
  }
  if (bulkImported === false) {
    return (
      <span
        className="text-slate-500 text-xs"
        title="Created via the regular quote / issuance flow"
      >
        Manual
      </span>
    );
  }
  // null / undefined → pre-flag legacy data, classification unknown.
  return (
    <span
      className="text-slate-400 text-xs"
      title="Created before the bulk-import flag existed — classification unknown"
    >
      -
    </span>
  );
};

export default BulkUploadBadge;
